import {
    PWA_CLIENT_PWA_OPEN_THROTTLE_MS,
    PWA_CLIENT_SYNC_THROTTLE_MS,
    PWA_CLIENT_SYNCED_STORAGE_KEY_PREFIX,
} from '../constants';
import { PwaClient } from '../types';

interface IStoredClientSync {
    client: PwaClient;
    at: number;
}

export function getClientSyncStorageKey(userId: string): string {
    return `${PWA_CLIENT_SYNCED_STORAGE_KEY_PREFIX}${userId}`;
}

export function readLastClientSync(userId: string, storage: Storage = localStorage): IStoredClientSync | null {
    try {
        const raw = storage.getItem(getClientSyncStorageKey(userId));
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<IStoredClientSync>;
        if ((parsed.client !== 'web' && parsed.client !== 'pwa') || typeof parsed.at !== 'number') {
            return null;
        }

        return { client: parsed.client, at: parsed.at };
    } catch {
        return null;
    }
}

export function markClientSynced(
    userId: string,
    client: PwaClient,
    now: number = Date.now(),
    storage: Storage = localStorage
): void {
    try {
        storage.setItem(getClientSyncStorageKey(userId), JSON.stringify({ client, at: now }));
    } catch {
        // storage full / disabled — throttling is best effort only
    }
}

/**
 * Sync when we have never reported for this user, when the client changed
 * (browser -> installed PWA or back), or when the last report went stale.
 *
 * PWA uses a shorter window so admin `last_pwa_open_at` stays meaningful when
 * the user re-opens the installed app without logging in again.
 */
export function shouldSyncPwaClient(params: {
    userId: string | undefined;
    client: PwaClient;
    now?: number;
    storage?: Storage;
    throttleMs?: number;
}): boolean {
    const { userId, client, now = Date.now(), storage = localStorage } = params;

    if (!userId) {
        return false;
    }

    const throttleMs =
        params.throttleMs ??
        (client === 'pwa' ? PWA_CLIENT_PWA_OPEN_THROTTLE_MS : PWA_CLIENT_SYNC_THROTTLE_MS);

    const last = readLastClientSync(userId, storage);
    if (!last) {
        return true;
    }

    if (last.client !== client) {
        return true;
    }

    return now - last.at >= throttleMs;
}
